import { createPortal } from 'react-dom';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { yupResolver } from '@hookform/resolvers/yup';
import { UserPlus } from 'lucide-react';

import {
	ButtonGroup,
	DialogIcon,
	DialogTitle,
	PunchupDialogue,
} from 'features/common/components/Punchup/composites/dialogs/PunchupDialogue';
import { PunchupButton } from 'features/common/components/Punchup/primitives/buttons/PunchupButton';
import { PunchupInput } from 'features/common/components/Punchup/primitives/forms/fields/PunchupInput';
import { postToast } from 'features/common/utils/postFlash';
import { airbrake } from 'ErrorBoundary';

import { inviteScanner } from '../services';
import { PARTNER_MEMBERS_KEY } from '../hooks/useGatesManagement';
import { AddScannerFormData } from '../types';
import { addScannerSchema } from './addScannerSchema';
import styles from '../PunchupGatesManagement.module.css';

interface AddScannerDialogProps {
	onClose: () => void;
}

/** Invite a brand-new scanner (admin-only — the parent gates the entry by role). Sends the corrected
 * `roles:['scanner']` payload via `inviteScanner`; on a non-admin 401/403 the onError toast explains why. */
export const AddScannerDialog = ({ onClose }: AddScannerDialogProps) => {
	const { t } = useTranslation();
	const queryClient = useQueryClient();
	const methods = useForm<AddScannerFormData>({
		defaultValues: { full_name: '', email: '' },
		resolver: yupResolver(addScannerSchema(t)),
	});
	const { errors } = methods.formState;

	const invite = useMutation({
		mutationFn: (data: AddScannerFormData) => inviteScanner(data),
		onSuccess: () => {
			queryClient.invalidateQueries({ queryKey: PARTNER_MEMBERS_KEY });
			postToast('success', t('gates-invite-success', 'Scanner invited'));
			onClose();
		},
		onError: (error) => {
			airbrake.notify({ error: 'Error inviting scanner', context: { error } });
			postToast('error', t('gates-invite-error', 'Could not invite the scanner. Only admins can invite new staff.'));
		},
	});

	return createPortal(
		<PunchupDialogue maxWidth={420} isLoading={invite.isLoading} onClose={onClose}>
			<DialogIcon>
				<UserPlus size={24} />
			</DialogIcon>
			<DialogTitle>{t('gates-invite-title', 'Invite a scanner')}</DialogTitle>
			<FormProvider {...methods}>
				<form onSubmit={methods.handleSubmit((data) => invite.mutate(data))} className={styles.dialogForm}>
					<PunchupInput
						id="scanner-name"
						name="full_name"
						label={t('gates-invite-name', 'Full name')}
						required
						errorProp={errors.full_name?.message}
					/>
					<PunchupInput
						id="scanner-email"
						name="email"
						label={t('gates-invite-email', 'Email')}
						required
						errorProp={errors.email?.message}
					/>
					<ButtonGroup>
						<PunchupButton
							type="button"
							variant="secondary"
							text={t('button-cancel', 'Cancel')}
							onClick={onClose}
							disabled={invite.isLoading}
						/>
						<PunchupButton
							type="submit"
							variant="primary"
							text={t('gates-invite-cta', 'Send invite')}
							disabled={invite.isLoading}
						/>
					</ButtonGroup>
				</form>
			</FormProvider>
		</PunchupDialogue>,
		document.body
	);
};
